import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { lovable } from "@/integrations/lovable";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    banned: typeof search.banned === "string" ? search.banned : undefined,
  }),
  head: () =>
    seoHead({
      title: "ログイン | KAGEM",
      description: "KAGEMにログインしてAI変身動画・画像生成を続けましょう。",
      path: "/login",
      noindex: true,
    }),
  component: LoginPage,
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const redirectTo = useMemo(() => {
    const r = search.redirect;
    // Only allow internal paths, and never loop back to auth pages
    if (r && r.startsWith("/") && !r.startsWith("//") && !r.startsWith("/login") && !r.startsWith("/signup")) {
      return r;
    }
    return "/dashboard";
  }, [search.redirect]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Show banned toast once if redirected from _authenticated
  useEffect(() => {
    if (search.banned === "1") {
      toast.error("このアカウントは停止されています。お問い合わせください。");
    }
  }, [search.banned]);

  // If already logged in, redirect immediately
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted && data.session) {
        navigate({ to: redirectTo as never, replace: true });
      }
    });
    return () => {
      mounted = false;
    };
  }, [navigate, redirectTo]);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}${redirectTo}`,
      });
      if (result.error) {
        toast.error("Googleログインに失敗しました。もう一度お試しください。");
        setGoogleLoading(false);
        return;
      }
      if (!result.redirected) {
        track("login", { method: "google" });
        navigate({ to: redirectTo as never, replace: true });
      }
    } catch {
      toast.error("Googleログイン中にエラーが発生しました");
      setGoogleLoading(false);
    }
  };

  const handleApple = async () => {
    setAppleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: `${window.location.origin}${redirectTo}`,
      });
      if (result.error) {
        toast.error("Apple IDログインに失敗しました。もう一度お試しください。");
        setAppleLoading(false);
        return;
      }
      if (!result.redirected) {
        track("login", { method: "apple" });
        navigate({ to: redirectTo as never, replace: true });
      }
    } catch {
      toast.error("Apple IDログイン中にエラーが発生しました");
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError("メールアドレスの形式が正しくありません");
      return;
    }
    setEmailError(null);
    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        const msg = error.message?.toLowerCase() ?? "";
        if (msg.includes("invalid") || msg.includes("credentials")) {
          toast.error("メールアドレスまたはパスワードが正しくありません");
        } else if (msg.includes("confirm") || msg.includes("not confirmed")) {
          toast.error("メールアドレスの確認が完了していません。受信箱をご確認ください");
        } else if (msg.includes("network") || msg.includes("fetch")) {
          toast.error("ネットワークエラーが発生しました。接続をご確認ください");
        } else {
          toast.error(`ログインに失敗しました: ${error.message}`);
        }
        setSubmitting(false);
        return;
      }
      // banned チェック: ログイン成功直後に確認し、停止中なら即サインアウト
      const userId = data.user?.id;
      if (userId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("banned")
          .eq("user_id", userId)
          .maybeSingle();
        if (profile?.banned) {
          await supabase.auth.signOut();
          toast.error("このアカウントは停止されています。お問い合わせください。");
          setSubmitting(false);
          return;
        }
      }
      track("login", { method: "email" });
      toast.success("ログインしました");
      navigate({ to: redirectTo as never, replace: true });
    } catch {
      toast.error("ネットワークエラーが発生しました。接続をご確認ください");
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gradient-hero relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-10 sm:px-6">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Link to="/" aria-label="KAGEM">
            <BrandLogo size="lg" className="flex-col gap-2 [&_img]:h-20 [&_img]:w-20 sm:[&_img]:h-24 sm:[&_img]:w-24" />
          </Link>
        </div>

        <Card className="border-border/60 shadow-2xl shadow-brand-primary/10 backdrop-blur-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">ログイン</CardTitle>
            <CardDescription>アカウントにログインして続けましょう</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Button
              type="button"
              variant="outline"
              className="relative h-11 w-full border-2 font-medium"
              onClick={handleGoogle}
              disabled={googleLoading || appleLoading || submitting}
            >
              {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
              <span>Googleでログイン</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="relative h-11 w-full border-2 bg-black font-medium text-white hover:bg-black/90 hover:text-white dark:bg-white dark:text-black dark:hover:bg-white/90 dark:hover:text-black"
              onClick={handleApple}
              disabled={googleLoading || appleLoading || submitting}
            >
              {appleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleIcon />}
              <span>Apple IDでログイン</span>
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">または</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  aria-invalid={!!emailError}
                  required
                />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">パスワード</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary underline-offset-4 hover:underline"
                  >
                    パスワードをお忘れですか？
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "パスワードを非表示" : "パスワードを表示"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="h-11 w-full bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 hover:opacity-95"
                disabled={submitting || email.length === 0 || password.length === 0}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                ログイン
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                アカウントをお持ちでない方は{" "}
                <Link to="/signup" className="text-primary underline-offset-4 hover:underline">
                  新規登録
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z"/>
    </svg>
  );
}
